import { mixins } from 'vue-class-component';
import { Component, Inject, Prop } from 'vue-property-decorator';
import EvaluacionSnpMixin from '@/entities/snp/evaluacion/evaluacion-snp-mixin';
import CuestionarioSnpComponent from '@/entities/snp/snippets/cuestionario/cuestionario-snp.vue';
import { Solicitud } from '@/shared/model/solicitud.model';
import { Solucion } from '@/shared/model/solucion.model';
import { CuestionarioSnp, PreguntaCuestionarioSnp } from '@/shared/model/snp/cuestionario.model';
import { IDefinicionEvaluacion } from '@/shared/model/definicion-evaluacion.model';
import { IRevision } from '@/shared/model/revision.model';
import { EstadoRevision } from '@/shared/model/enumerations/estado-revision.model';
import SolicitudService from '@/entities/solicitud/solicitud.service';
import FirmarDictamenSnpComponent from '@/entities/snp/evaluador/dictamen/firmar-dictamen-snp.vue';
import { TipoComision } from '@/shared/model/enumerations/tipo-comision.model';
import { EstadoInsaculacion } from '@conacyt/insaculacion-client';

@Component({
  components: {
    'cuestionario-snp': CuestionarioSnpComponent,
    'firmar-dictamen-snp': FirmarDictamenSnpComponent,
  },
})
export default class ValoracionPlenariaSNPComponent extends mixins(EvaluacionSnpMixin) {
  @Inject('solicitudService') private readonly solicitudService: () => SolicitudService;

  @Prop({ default: false })
  public solicitud!: Solicitud;

  @Prop({ required: false })
  public solucion!: Solucion;

  public cuestionarioInicial: CuestionarioSnp = {};
  private iniciandoComonente = false;
  public preguntas: Array<PreguntaCuestionarioSnp> = [];
  public cuestionario: CuestionarioSnp = {};

  private buscandoDefinicionEvaluacion = false;
  private definicionEvaluacion: IDefinicionEvaluacion = {};
  private evaluacionPlenariaId = '';
  public evaluacionPlenariaDescripcion = '';
  public tipoComisionEvaluacionPlenaria: TipoComision = null;

  private obteniendoRevisionesIndividuales = false;
  private evaluacionesIndividualesId = [];
  public revisionesIndividuales: Array<IRevision> = [];

  private obteniendoRevisionPlenaria = false;
  public revisionEvaluador: IRevision = {};

  public guardandoEvaluacion = false;
  public verModalGuardar = false;

  public finalizandoEvaluacion = false;
  public verModalFinalizar = false;

  public estadoInsaculacion: EstadoInsaculacion = null;

  /**
   * Carga inicial
   */
  mounted() {
    this.iniciarComponente();
  }

  private iniciarComponente(): void {
    this.iniciandoComonente = true;
    const modalidad = this.solicitud?.programa?.cveModalidad;
    const params = this.solucion?.params;

    const cuestionariosPorModalidad: Record<string, any> = {
      ESC: params?.cuestionario_esc,
      'ENS REM': params?.cuestionario_ens_rem,
    };

    const cuestionario = modalidad ? cuestionariosPorModalidad[modalidad] : null;

    if (cuestionario?.preguntas?.length) {
      this.preguntas = cuestionario.preguntas.map(PreguntaCuestionarioSnp.fromObject);
    } else {
      this.alertService().showWarning(this, this.$t('apeironGwApp.valoracionPlenariaSNP.alerts.noPreguntasEncontradas').toString());
    }

    this.revisionEvaluador = {};
    this.definicionEvaluacion = {};
    this.evaluacionPlenariaId = '';
    this.evaluacionPlenariaDescripcion = '';

    this.revisionesIndividuales = [];

    this.obtenerEvaluacionDeSolucion();
    this.iniciandoComonente = false;
  }

  /**
   * Obtiene la definición de evaluación.
   * Ejemplo: evaluación individual dictaminadora
   */
  public obtenerEvaluacionDeSolucion(): void {
    this.buscandoDefinicionEvaluacion = true;
    this.solucionService()
      .findEvaluacionDefinicion(this.solucion?.id ?? '')
      .then(definicionEvaluacion => {
        this.definicionEvaluacion = definicionEvaluacion;

        for (const definicion of this.definicionEvaluacion.revisiones) {
          if (definicion.id == this.$route.params.evaluacionId) {
            this.evaluacionPlenariaId = definicion.id;
            this.evaluacionPlenariaDescripcion = definicion.descripcion;
            this.tipoComisionEvaluacionPlenaria = definicion.tipoComision;
            this.evaluacionesIndividualesId = definicion.revisionesSoloLectura;

            this.obtenerEstadoDeInsaculacionOError(this.tipoComisionEvaluacionPlenaria, this.solucion)
              .then(estadoInsaculacion => (this.estadoInsaculacion = estadoInsaculacion))
              .catch(error => {
                this.alertService().showHttpError(this, error.response);
              });
          }
        }
        if (!this.evaluacionPlenariaId) {
          this.alertService().showError(this, this.$t('apeironGwApp.valoracionPlenariaSNP.alerts.sinEvaluacionDefinida').toString());
        } else {
          this.obtenerRevisionPlenaria();
          this.obtenerRevisionesIndividuales();
        }
      })
      .catch(() => {
        this.alertService().showError(this, this.$t('apeironGwApp.valoracionPlenariaSNP.alerts.sinEvaluacionDefinida').toString());
        this.definicionEvaluacion = {};
      })
      .finally(() => {
        this.buscandoDefinicionEvaluacion = false;
      });
  }

  /**
   * Obtiene las respuestas de la evaluación del evaluador.
   */
  public obtenerRevisionPlenaria(): void {
    this.obteniendoRevisionPlenaria = true;
    const cvuPresidente = (this.solicitud.revisores ?? []).find(revisor => revisor.evaluacionId == this.evaluacionPlenariaId);
    this.revisionService()
      .findByEvaluacionIdAndRevisorIdAndSolicitudId(this.evaluacionPlenariaId, cvuPresidente.cvu, this.solicitud.id)
      .then(response => {
        this.revisionEvaluador = response;
        this.cuestionarioInicial = CuestionarioSnp.fromObject(this.revisionEvaluador.respuesta?.resultados ?? new CuestionarioSnp());
      })
      .catch(err => {
        if (err.response?.status != 404) {
          this.alertService().showError(
            this,
            err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionPlenariaSNP.alerts.ocurrioUnError').toString()
          );
        }
      })
      .finally(() => {
        this.obteniendoRevisionPlenaria = false;
      });
  }

  public obtenerRevisionesIndividuales(): void {
    this.obteniendoRevisionesIndividuales = true;
    this.revisionesIndividuales = [];

    const evaluaciones = (this.solicitud.revisores ?? []).filter(revisor => this.evaluacionesIndividualesId.includes(revisor.evaluacionId));
    let contadorPeticiones = 0;

    if (evaluaciones.length > 0) {
      for (const evaluacion of evaluaciones) {
        contadorPeticiones += 1;
        this.revisionService()
          .findByEvaluacionIdAndRevisorIdAndSolicitudId(evaluacion.evaluacionId, evaluacion.cvu, this.solicitud.id)
          .then(response => {
            this.revisionesIndividuales.push(response);
          })
          .catch(err => {
            if (err.response?.status != 404) {
              this.alertService().showError(
                this,
                err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionPlenariaSNP.alerts.ocurrioUnError').toString()
              );
            }
          })
          .finally(() => {
            contadorPeticiones -= 1;

            if (contadorPeticiones == 0) {
              this.obteniendoRevisionesIndividuales = false;
            }
          });
      }
    } else {
      this.obteniendoRevisionesIndividuales = false;
    }
  }

  public manejaFormularioCambia(cuestionario: CuestionarioSnp): void {
    this.cuestionario = cuestionario;
  }

  public mostrarModalGuardar(): void {
    this.verModalGuardar = true;
  }

  /**
   * Guarda las respuestas del cuestionario en el sistema.
   */
  public guardarEvaluacion(): void {
    if (EstadoInsaculacion.Aprobada != this.estadoInsaculacion || EstadoRevision.ASIGNADA != this.revisionEvaluador.estado) {
      return;
    }
    this.guardandoEvaluacion = true;
    this.verModalGuardar = false;
    this.revisionEvaluador.respuesta.resultados = this.cuestionario;
    this.revisionService()
      .update(this.revisionEvaluador)
      .then(() => {
        this.obtenerRevisionPlenaria();
        this.alertService().showSuccess(this, this.$t('apeironGwApp.valoracionPlenariaSNP.alerts.evaluacionGuardada').toString());
      })
      .catch(err => {
        this.alertService().showError(
          this,
          err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionPlenariaSNP.alerts.ocurrioUnError').toString()
        );
      })
      .finally(() => {
        this.guardandoEvaluacion = false;
      });
  }

  public mostrarModalFinalizar(): void {
    this.verModalFinalizar = true;
  }

  /**
   * Actualiza el estado de la evaluación a EVALUADA
   * Como acción de finalizar evaluación.
   */
  public finalizarEvaluacion(): void {
    if (EstadoInsaculacion.Aprobada != this.estadoInsaculacion || EstadoRevision.ASIGNADA != this.revisionEvaluador.estado) {
      return;
    }
    this.verModalFinalizar = false;
    if (this.puedeFinalizar) {
      this.finalizandoEvaluacion = true;
      this.revisionEvaluador.estado = EstadoRevision.REVISADA;
      this.revisionEvaluador.respuesta.resultados = this.cuestionario;
      this.revisionService()
        .update(this.revisionEvaluador)
        .then(() => {
          this.solicitudService()
            .find(this.revisionEvaluador.solicitudResumen.solicitudId)
            .then(res => {
              const revisor = (res.revisores ?? []).find(
                revisor => revisor.cvu == this.revisionEvaluador.revisor.revisorId && this.evaluacionPlenariaId == revisor.evaluacionId
              );
              revisor.estado = EstadoRevision.REVISADA;
              this.solicitudService()
                .updateRevisores(res)
                .then(() => {
                  this.insaculacionesApi()
                    .obtenerSolicitudSnp(this.revisionEvaluador.solicitudResumen.solicitudId)
                    .then(response => {
                      if (this.tipoComisionEvaluacionPlenaria == TipoComision.DICTAMINADORA) {
                        response.data.revisionPlenariaDictaminacion = EstadoRevision.REVISADA;
                      } else {
                        response.data.revisionPlenariaReconsideracion = EstadoRevision.REVISADA;
                      }
                      this.insaculacionesApi().actualizarSolicitudSnp(response.data.id, response.data);
                    });
                })
                .catch(err => {
                  this.alertService().showError(
                    this,
                    err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionIndividualSNP.alerts.ocurrioUnError').toString()
                  );
                });
            })
            .catch(err => {
              this.alertService().showError(
                this,
                err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionIndividualSNP.alerts.ocurrioUnError').toString()
              );
            });
          this.obtenerRevisionPlenaria();
          this.alertService().showSuccess(this, this.$t('apeironGwApp.valoracionPlenariaSNP.alerts.evaluacionFinalizada').toString());
        })
        .catch(err => {
          this.alertService().showError(
            this,
            err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionPlenariaSNP.alerts.ocurrioUnError').toString()
          );
        })
        .finally(() => {
          this.finalizandoEvaluacion = false;
        });
    }
  }

  get obtenerUrlDeDictamen(): string {
    if (!!this.revisionEvaluador.respuesta) {
      if (this.tipoComisionEvaluacionPlenaria == TipoComision.DICTAMINADORA) {
        return !!this.revisionEvaluador.respuesta['documentoDictamen'] && !!this.revisionEvaluador.respuesta['documentoDictamen']['url']
          ? this.revisionEvaluador.respuesta['documentoDictamen']['url']
          : undefined;
      } else {
        return !!this.revisionEvaluador.respuesta['documentoFormalizacion'] &&
          !!this.revisionEvaluador.respuesta['documentoFormalizacion']['url']
          ? this.revisionEvaluador.respuesta['documentoFormalizacion']['url']
          : undefined;
      }
    } else {
      return undefined;
    }
  }

  get mostrarOverlay(): boolean {
    return (
      this.guardandoEvaluacion ||
      this.obteniendoRevisionPlenaria ||
      this.buscandoDefinicionEvaluacion ||
      this.iniciandoComonente ||
      this.finalizandoEvaluacion ||
      this.obteniendoRevisionesIndividuales
    );
  }

  get puedeFinalizar(): boolean {
    return this.cuestionario.estaCompleto;
  }
}
